import React from 'react'
import NumberFormat from 'react-number-format'
import { OrderModel } from '../../../models/transaction'
import {
  ActionTypes,
  TransactionStatus,
} from '../../../util/transactionConstants'
import { round } from '../../../util/helpers'
import { PaymentMethods } from '../../../models/payment-methods'
import CurrencyFormat from '../../../components/common/CurrencyFormat'

export const setupCartItems = items =>
  // setups help properties and calculate total sum and vat based on current cartItem details
  items.map((item, index) =>
    Object.assign(new OrderModel(), item, {
      newQuantity: 0,
      newQuantityValid: true,
      reservedQuantityValid: true,
      newAmount: 0,
      newSum: 0,
      newSumValid: true,
      newSumDescription: '',
      rowId: index,
    }),
  )

export const FormattedAmount = amount => <CurrencyFormat value={amount} />

export const FormattedUnits = (number, unit) =>
  (number || number === 0) && (
    <NumberFormat
      value={number}
      displayType="text"
      thousandSeparator=" "
      decimalSeparator=","
      decimalScale={3}
      suffix={` ${unit}`}
    />
  )

export const unitPriceInkVat = (unitPriceExVat, vatRate) =>
  parseFloat(unitPriceExVat) + unitPriceExVat * vatRate

export const calculateVatSum = (unitPriceIncVat, vatRate) =>
  unitPriceIncVat - unitPriceIncVat / (1 + vatRate)

export const itemSumIncVat = (quantity, unitPriceExVat, vatRate) =>
  quantity * unitPriceExVat * vatRate + quantity * unitPriceExVat

export const itemSumExVat = (quantity, unitPriceExVat) =>
  quantity * unitPriceExVat

export const calculateVats = (items, addVat = 0) => {
  const totalVat = items.map(
    item => item.totalAmountIncVat - item.totalAmountExVat,
  )
  return totalVat.reduce(
    (prevValue, currentValue) => prevValue + currentValue,
    addVat,
  )
}

const formatQuantity = (quantity, allowNegative) => {
  const formattedQuantity = +quantity.toFixed(2).replace(/(.*?)0*$/g, '$1')
  return allowNegative || +formattedQuantity > 0 ? `${formattedQuantity}` : '0'
}

const getMaxQuantityForDebitOrAnnul = (item, allowNegative) =>
  formatQuantity(
    item.reservedQuantity - item.debitedQuantity - item.canceledQuantity,
    allowNegative,
  )

const getMaxQuantityForCredit = (item, allowNegative) =>
  formatQuantity(item.debitedQuantity - item.creditedQuantity, allowNegative)

export const getMaxQuantity = (item, action, allowNegative) => {
  // returns the maximum value to be debited/credited/canceled based on the current action
  switch (action) {
    case ActionTypes.DEBIT:
    case ActionTypes.ANNUL:
      return getMaxQuantityForDebitOrAnnul(item, allowNegative)
    case ActionTypes.CREDIT:
      return getMaxQuantityForCredit(item, allowNegative)
    case ActionTypes.CREDIT_AMOUNT:
      return item.totalAmountIncVat
    default:
      return 0
  }
}

export const getActionColor = (amount, action) => {
  const defaultCssProperty = 'inherit'
  switch (action) {
    case ActionTypes.DEBIT:
      return amount > 0 ? defaultCssProperty : 'lightgrey'
    case ActionTypes.CREDIT:
      return amount > 0 ? defaultCssProperty : 'lightgrey'
    case ActionTypes.ANNUL:
      return amount > 0 ? defaultCssProperty : 'lightgrey'
    default:
      return defaultCssProperty
  }
}

export const calculateActionSum = (articles = [], sumOperations = null) => {
  if (sumOperations) {
    return +articles[articles.length - 1].newSum || 0
  }

  return round(
    articles.reduce((prev, curr) => {
      if (curr.newQuantity) {
        return prev + curr.unitPriceIncVat * curr.newQuantity
      }
      return prev
    }, 0),
    2,
  )
}

const getMax = limit => (limit < 0 ? 0 : limit)
const getMin = limit => (limit > 0 ? 0 : limit)

const isWithin = (quantity, limit) => {
  const max = getMax(limit)
  const min = getMin(limit)
  return quantity <= max && quantity >= min
}

export const isQuantityValidAgainstCartStatus = (
  actionType,
  item,
  articles,
  newQuantity,
  cartStatus,
  allowZero,
) => {
  const sum = calculateActionSum(articles)
  if (actionType === ActionTypes.DEBIT) {
    const limit = +getMaxQuantityForDebitOrAnnul(item, true)
    return (
      isWithin(newQuantity, limit) &&
      (sum > 0 || (allowZero && newQuantity > 0)) &&
      sum <= cartStatus.debitableAmount
    )
  }
  if (actionType === ActionTypes.CREDIT) {
    const limit = +getMaxQuantityForCredit(item, true)
    return (
      isWithin(newQuantity, limit) &&
      (sum > 0 || (allowZero && newQuantity > 0)) &&
      sum <= cartStatus.creditableAmount
    )
  }
  if (actionType === ActionTypes.ANNUL) {
    const limit = +getMaxQuantityForDebitOrAnnul(item, true)
    return isWithin(newQuantity, limit)
  }

  return true
}

export const disableCheckbox = (
  transaction,
  hasInvoiceNumber,
  invoiceNumbers,
) =>
  transaction.resultType !== 'PAYMENT' ||
  transaction.status === TransactionStatus.FROZEN ||
  (transaction.payment &&
    transaction.payment.paymentStatus &&
    transaction.payment.paymentStatus.debitableAmount === 0) ||
  (transaction.paymentMethod &&
    transaction.paymentMethod.type === PaymentMethods.RESURS_INVOICE &&
    !hasInvoiceNumber &&
    !invoiceNumbers[transaction.id])

export const debit = (
  items,
  transactions,
  hasInvoiceNumber,
  invoiceNumbers,
  debitFunc,
) => {
  const toDebit = []
  items.keys.forEach(i => {
    const t = transactions.filter(transaction => transaction.id === i)[0]
    if (t) {
      toDebit.push(t)
    }
  })
  const invoices = toDebit.filter(
    deb => deb.paymentMethod.type === PaymentMethods.RESURS_INVOICE,
  )
  const notInvoices = toDebit.filter(
    d => d.paymentMethod.type !== PaymentMethods.RESURS_INVOICE,
  )
  if (!hasInvoiceNumber) {
    const hasInvoice = []
    invoices.forEach(invoice => {
      Object.keys(invoiceNumbers).forEach(number => {
        if (invoice.id === number && invoiceNumbers[number] !== '') {
          hasInvoice.push({
            ...invoice,
            invoiceNumber: invoiceNumbers[number],
          })
        }
      })
    })
    if (hasInvoice.length === invoices.length) {
      debitFunc(hasInvoice, notInvoices)
    }
  } else {
    debitFunc(invoices, notInvoices)
  }
}
